import React, { PropsWithChildren, createContext, useContext, useState, useCallback } from 'react';
import { EditionFormDataType, STEPS } from '../types';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

export interface IEditionFormWrapperProps {
  initialState?: EditionFormDataType;
  updateId?: string | null;
  onSave: (updatedState?: EditionFormDataType) => Promise<void>;
}

export interface IEditionFormContext {
  updateId: string | null;
  isDirty: boolean;
  activeStep: number;
  lastStep: number;
  formState: EditionFormDataType;
  errors: Record<string, boolean>;
  setErrors: (id: string, errors: boolean) => void;
  setActiveStep: (step: STEPS) => void;
  updateState: (value: Partial<EditionFormDataType>) => void;
  resetForm: () => void;
  updateIsDirty: (value: boolean) => void;
  handleSave: () => Promise<void>;
}

const EditionFormContext = createContext<IEditionFormContext | null>(null);

export const DEFAULT_STATE: (t: TFunction) => EditionFormDataType = (t: TFunction) => ({
  id: '',
  name: '',
  type: t('documents.edition.modal.sites'),
  file: null,
  sf_account_ids: []
});

export const useEditionFormContext = () => {
  const ctx = useContext(EditionFormContext);

  if (!ctx) {
    throw Error('useEditionFormContext must be used inside EditionFormWrapper balise');
  }

  return ctx;
};

const EditionFormWrapper: React.FC<PropsWithChildren<IEditionFormWrapperProps>> = ({
  initialState,
  updateId: initialUpdateId = null,
  onSave,
  children
}) => {
  const { t } = useTranslation();
  const [errors, setErrors] = useState<IEditionFormContext['errors']>({});
  const [updateId] = useState(initialUpdateId);
  const [activeStep, setActiveStepNumber] = useState(0);
  const [lastStep, setLastStepNumber] = useState(0);
  const [formState, setFormState] = useState<EditionFormDataType>(initialState || DEFAULT_STATE(t));
  const [isDirty, setIsDirty] = useState(false);

  const updateState = (value: Partial<EditionFormDataType>) => {
    if (!isDirty) {
      setIsDirty(true);
    }

    setFormState(form => ({ ...form, ...value }));
  };

  const updateIsDirty = (value: boolean) => {
    if (isDirty !== value) {
      setIsDirty(value);
    }
  };

  const resetForm = () => {
    setActiveStep(0);
    setFormState(DEFAULT_STATE(t));
  };

  const setActiveStep = (value: STEPS) => {
    if (value > lastStep) {
      setLastStepNumber(value);
    }
    setActiveStepNumber(value);
  };

  const setStepErrors = (id: string, errors: boolean) => {
    setErrors(v => ({ ...v, [id]: errors }));
  };

  const handleSave = useCallback(async (updatedState?: EditionFormDataType) => {
    await onSave(updatedState || formState);
  }, [onSave, formState]);

  const getValue = useCallback(() => ({
    updateId,
    isDirty,
    lastStep,
    activeStep,
    formState,
    errors,
    setErrors: setStepErrors,
    updateState,
    setActiveStep,
    updateIsDirty,
    resetForm,
    handleSave
  }), [updateId, isDirty, lastStep, activeStep, formState, errors, updateState, updateIsDirty, resetForm, handleSave]);

  return <EditionFormContext.Provider value={getValue()}>{children}</EditionFormContext.Provider>;
};

export default EditionFormWrapper;